import React, { useState, useRef } from 'react';
import { Mic, Square, Play, Pause, Trash2 } from 'lucide-react';

const AudioRecorder = ({ onRecord, onClear, existingUrl, onDeleteExisting }) => {
  const [isRecording, setIsRecording] = useState(false);
  const [recordedBlob, setRecordedBlob] = useState(null);
  const [playing, setPlaying] = useState(false);
  const [duration, setDuration] = useState(0);
  const mediaRecorderRef = useRef(null);
  const chunksRef = useRef([]);
  const timerRef = useRef(null);
  const audioRef = useRef(null);

  const startRecording = async () => {
    try {
      const stream = await navigator.mediaDevices.getUserMedia({ audio: true });
      const mediaRecorder = new MediaRecorder(stream);
      mediaRecorderRef.current = mediaRecorder;
      chunksRef.current = [];

      mediaRecorder.ondataavailable = (e) => {
        if (e.data.size > 0) chunksRef.current.push(e.data);
      };

      mediaRecorder.onstop = () => {
        const blob = new Blob(chunksRef.current, { type: 'audio/webm' });
        const file = new File([blob], `recorded_${Date.now()}.webm`, { type: 'audio/webm' });
        setRecordedBlob(file);
        onRecord(file);
        stream.getTracks().forEach(t => t.stop());
      };

      mediaRecorder.start();
      setIsRecording(true);
      setDuration(0);
      timerRef.current = setInterval(() => setDuration(d => d + 1), 1000);
    } catch (err) {
      alert('Microphone non disponible. Veuillez autoriser l\'accès au micro.');
    }
  };

  const stopRecording = () => {
    if (mediaRecorderRef.current && isRecording) {
      mediaRecorderRef.current.stop();
      setIsRecording(false);
      clearInterval(timerRef.current);
    }
  };

  const togglePlay = () => {
    if (!audioRef.current || !recordedBlob) return;
    if (playing) {
      audioRef.current.pause();
      setPlaying(false);
    } else {
      audioRef.current.src = URL.createObjectURL(recordedBlob);
      audioRef.current.play();
      setPlaying(true);
    }
  };

  const clearRecorded = () => {
    setRecordedBlob(null);
    setPlaying(false);
    onClear?.();
  };

  const formatTime = (s) => {
    const m = Math.floor(s / 60);
    const sec = s % 60;
    return `${m}:${sec.toString().padStart(2, '0')}`;
  };

  if (existingUrl && !recordedBlob) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', background: '#E8F5E9', borderRadius: '8px' }}>
        <audio ref={audioRef} src={existingUrl} onEnded={() => setPlaying(false)} />
        <button onClick={() => {
          if (audioRef.current) {
            if (playing) { audioRef.current.pause(); setPlaying(false); }
            else { audioRef.current.play(); setPlaying(true); }
          }
        }} style={{ width: '28px', height: '28px', borderRadius: '50%', background: 'linear-gradient(135deg, #2E7D32, #4CAF50)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {playing ? <Pause size={12} color="white" /> : <Play size={12} color="white" style={{ marginLeft: '1px' }} />}
        </button>
        <span style={{ fontSize: '12px', color: '#555', flex: 1 }}>Audio existant</span>
        <button onClick={onDeleteExisting} style={{ background: '#ffebee', border: 'none', borderRadius: '6px', padding: '4px 8px', cursor: 'pointer' }}>
          <Trash2 size={12} color="#E53935" />
        </button>
        <button onClick={startRecording} style={{ background: 'none', border: '1px solid #2E7D32', color: '#2E7D32', borderRadius: '6px', padding: '4px 10px', cursor: 'pointer', fontSize: '11px', fontWeight: '600' }}>
          <Mic size={12} style={{ display: 'inline', verticalAlign: 'middle', marginRight: '3px' }} /> Nouveau
        </button>
      </div>
    );
  }

  if (recordedBlob) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', background: '#E3F2FD', borderRadius: '8px' }}>
        <audio ref={audioRef} onEnded={() => setPlaying(false)} />
        <button onClick={togglePlay} style={{ width: '28px', height: '28px', borderRadius: '50%', background: 'linear-gradient(135deg, #1565C0, #42A5F5)', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          {playing ? <Pause size={12} color="white" /> : <Play size={12} color="white" style={{ marginLeft: '1px' }} />}
        </button>
        <span style={{ fontSize: '12px', color: '#1565C0', flex: 1, fontWeight: '500' }}>Audio enregistré</span>
        <button onClick={clearRecorded} style={{ background: 'none', border: 'none', cursor: 'pointer', color: '#999' }}>
          <Trash2 size={14} />
        </button>
      </div>
    );
  }

  if (isRecording) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', gap: '10px', padding: '8px 12px', background: '#ffebee', borderRadius: '8px' }}>
        <div style={{ width: '10px', height: '10px', borderRadius: '50%', background: '#E53935', animation: 'pulse 1s infinite' }} />
        <span style={{ fontSize: '12px', color: '#E53935', fontWeight: '600' }}>Enregistrement...</span>
        <span style={{ fontSize: '13px', color: '#555', fontWeight: '700', minWidth: '40px', textAlign: 'center' }}>{formatTime(duration)}</span>
        <button onClick={stopRecording} style={{ width: '28px', height: '28px', borderRadius: '50%', background: '#E53935', border: 'none', cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
          <Square size={12} color="white" />
        </button>
        <style>{`@keyframes pulse { 0% { opacity: 1; } 50% { opacity: 0.3; } 100% { opacity: 1; } }`}</style>
      </div>
    );
  }

  return (
    <button onClick={startRecording}
      style={{ display: 'inline-flex', alignItems: 'center', gap: '6px', background: 'none', border: '1.5px dashed #e0e0e0', borderRadius: '8px', padding: '8px 14px', cursor: 'pointer', fontSize: '12px', color: '#666', fontWeight: '500' }}>
      <Mic size={14} /> Enregistrer un audio
    </button>
  );
};

export default AudioRecorder;
